import type { OdooTask } from '@/types/odoo';

// ── Types ──────────────────────────────────────────────────────────────

export interface ProjectleiderGroup {
  id: number;
  name: string;
  installerRows: InstallerRow[];
}

export interface InstallerRow {
  id: number;
  name: string;
  cells: Map<string, OdooTask[]>;
}

export interface WeekInfo {
  days: Date[];
  weekNumber: number;
  year: number;
}

// ── Constants ──────────────────────────────────────────────────────────

const DUTCH_DAYS = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'] as const;

const DUTCH_MONTHS = [
  'jan', 'feb', 'mrt', 'apr', 'mei', 'jun',
  'jul', 'aug', 'sep', 'okt', 'nov', 'dec',
] as const;

/** Card background colors keyed by stage name. */
export const PLANNING_COLORS: Record<string, string> = {
  'Nieuw': 'bg-blue-100',
  'Ingepland': 'bg-yellow-100',
  'In uitvoering': 'bg-orange-100',
  'Klaar': 'bg-green-100',
  'Geannuleerd': 'bg-gray-100',
};

/**
 * PL colour by projectleider name — the one place the planning's PL colours live.
 * Also used by the administratie list's werf headers.
 */
export function projectleiderColorByName(name: string): { bg: string; border: string; dot: string } {
  const plName = name.toLowerCase();
  if (plName.includes('jorn'))  return { bg: 'bg-gray-200',  border: 'border-l-gray-500',  dot: 'bg-gray-500' };
  if (plName.includes('david')) return { bg: 'bg-blue-100',  border: 'border-l-blue-500',   dot: 'bg-blue-500' };
  return { bg: 'bg-amber-100', border: 'border-l-amber-500', dot: 'bg-amber-500' };
}

/** PL-based card color for "Installateurs" mode */
export function getProjectleiderColor(task: OdooTask): { bg: string; border: string; dot: string } {
  return projectleiderColorByName(task.x_studio_projectleider ? task.x_studio_projectleider[1] : '');
}


// ── ISO week helpers ───────────────────────────────────────────────────

/** Return the ISO week number for a given date. */
export function getISOWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // Set to nearest Thursday: current date + 4 - current day number (Mon=1..Sun=7)
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

/** Return the ISO week-year for a given date. */
function getISOWeekYear(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  return d.getUTCFullYear();
}

// ── Public functions ───────────────────────────────────────────────────

/**
 * Get the Mon-Sat dates for the week at the given offset from the current week.
 * weekOffset=0 is the current week, -1 is last week, +1 is next week, etc.
 */
export function getWeekDates(weekOffset: number): WeekInfo {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Find Monday of the current week
  const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ...
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(today);
  monday.setDate(today.getDate() + diffToMonday + weekOffset * 7);

  // Build Mon–Sat (6 days)
  const days: Date[] = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }

  return {
    days,
    weekNumber: getISOWeekNumber(monday),
    year: getISOWeekYear(monday),
  };
}

/**
 * Format a week label in Dutch.
 * Example: "Week 12: 16 mrt – 21 mrt"
 */
export function formatWeekLabel(weekNumber: number, days: Date[]): string {
  if (days.length === 0) return `Week ${weekNumber}`;

  const first = days[0];
  const last = days[days.length - 1];

  const startStr = `${first.getDate()} ${DUTCH_MONTHS[first.getMonth()]}`;
  const endStr = `${last.getDate()} ${DUTCH_MONTHS[last.getMonth()]}`;

  return `Week ${weekNumber}: ${startStr} \u2013 ${endStr}`;
}

/**
 * Format a short day label in Dutch.
 * Example: "Ma 16 mrt"
 */
export function getDayLabel(date: Date): string {
  const dayName = DUTCH_DAYS[date.getDay()];
  const monthName = DUTCH_MONTHS[date.getMonth()];
  return `${dayName} ${date.getDate()} ${monthName}`;
}

/** Check whether a date is today (local time). */
export function isToday(date: Date): boolean {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

/** Format a date as "YYYY-MM-DD" for use as a Map key. */
export function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Group planning tasks into a grid structure:
 *   ProjectleiderGroup[] → InstallerRow[] → cells (dateKey → tasks)
 *
 * Grouping logic:
 * 1. Group by x_studio_projectleider (Many2one [id,name] or false → "Geen projectleider")
 * 2. Within each group, group by x_studio_installateur IDs resolved via userMap
 *    (empty array or missing → "Niet toegewezen")
 * 3. Only include tasks whose date_deadline falls within weekDays
 */
const PL_HOLIDAYS_PROJECT = 955;

/** EasyKit grouping config, provided by the API so the frontend carries no
 *  hardcoded Odoo IDs of its own. `groupTasksForGrid` falls back to safe
 *  no-ops (no special Hendrika section, no installer suppression) when the
 *  server omits it — e.g. older API builds. */
export interface EasykitGroupingConfig {
  plId: number;
  installerIds: number[];
  fullyEasykitInstallerIds: number[];
}

/** Is this a feestdag column marker (no installers, holiday project)? */
export function isFeestdagMarker(t: OdooTask): boolean {
  return !!t.project_id && t.project_id[0] === PL_HOLIDAYS_PROJECT && !t.x_studio_installateur?.length;
}

/** Is this a verlof task (has installers, holiday project, no PL)? */
export function isVerlofTask(t: OdooTask): boolean {
  return !!t.project_id && t.project_id[0] === PL_HOLIDAYS_PROJECT
    && !!t.x_studio_installateur?.length && !t.x_studio_projectleider;
}

export interface FeestdagInfo {
  name: string;
  taskId: number;
}

/** Detect feestdag marker tasks and return their date keys + info. */
export function getFeestdagDates(tasks: OdooTask[]): Map<string, FeestdagInfo> {
  const dates = new Map<string, FeestdagInfo>();
  for (const t of tasks) {
    if (isFeestdagMarker(t) && t.date_deadline) {
      dates.set(t.date_deadline.slice(0, 10), { name: t.name, taskId: t.id });
    }
  }
  return dates;
}

export function groupTasksForGrid(
  tasks: OdooTask[],
  weekDays: Date[],
  userMap: Map<number, string>,
  projectleiderMap: Map<number, string>,
  showAllInstallers?: boolean,
  easykitConfig?: EasykitGroupingConfig | null,
): ProjectleiderGroup[] {
  const EASYKIT_PL_ID = easykitConfig?.plId ?? -1;  // -1 → no-match sentinel
  const EASYKIT_INSTALLERS = new Set<number>(
    easykitConfig?.installerIds ?? [],
  );
  const FULLY_EASYKIT_INSTALLERS = new Set<number>(
    easykitConfig?.fullyEasykitInstallerIds ?? [],
  );
  // Build set of valid date keys for this week
  const validDateKeys = new Set(weekDays.map(formatDateKey));

  // Filter tasks to only those in this week, exclude feestdag markers (no installers)
  const weekTasks = tasks.filter(
    (t) => typeof t.date_deadline === 'string'
      && validDateKeys.has(t.date_deadline.slice(0, 10))
      && !isFeestdagMarker(t),
  );

  // Verlof tasks without PL should appear in every PL group (one task in Odoo, visible everywhere)

  // Intermediate: projectleider id → { name, installers: Map<installerId, Map<dateKey, tasks[]>> }
  const plMap = new Map<
    number,
    {
      name: string;
      installers: Map<number, { name: string; cells: Map<string, OdooTask[]> }>;
    }
  >();

  const ensurePl = (id: number, name: string) => {
    if (!plMap.has(id)) {
      plMap.set(id, { name, installers: new Map() });
    }
    return plMap.get(id)!;
  };

  const ensureInstaller = (
    pl: ReturnType<typeof ensurePl>,
    installerId: number,
    installerName: string,
  ) => {
    if (!pl.installers.has(installerId)) {
      pl.installers.set(installerId, { name: installerName, cells: new Map() });
    }
    return pl.installers.get(installerId)!;
  };

  for (const [plId, plName] of projectleiderMap) {
    ensurePl(plId, plName);
  }

  const verlofShared: OdooTask[] = [];
  const allePlShared: OdooTask[] = [];
  // (installerId, dateKey) pairs with any EasyKit activity — drives the
  // fourth pass that injects one "Easykit tour" summary card per
  // installer-day into the non-EasyKit PL sections.
  const easykitTourPairs = new Map<string, OdooTask[]>();

  // EasyKit detail always lands under Hendrika's section. Prefer the name
  // carried on the task itself so the group header stays accurate even if
  // Odoo renames her; fall back to a sensible default if no EasyKit task
  // this week has a projectleider set (e.g. 955 holiday shapes).
  let easykitPlName = 'Hendrika Perpet';
  for (const t of weekTasks) {
    if (t.is_easykit && t.x_studio_projectleider) {
      easykitPlName = t.x_studio_projectleider[1];
      break;
    }
  }

  for (const task of weekTasks) {
    const dateKey = (task.date_deadline as string).slice(0, 10);

    if (task.is_easykit && easykitConfig) {
      const instIds = task.x_studio_installateur?.length ? task.x_studio_installateur : [0];
      if (!isVerlofTask(task)) {
        for (const instId of instIds) {
          const pairKey = `${instId}:${dateKey}`;
          const existing = easykitTourPairs.get(pairKey);
          if (existing) existing.push(task);
          else easykitTourPairs.set(pairKey, [task]);
        }
      }

      // Place the full-detail card under Hendrika's group directly, even
      // when the task has no x_studio_projectleider set (some 955 holiday
      // tasks are modelled as verlof without a PL). `continue` so the task
      // never leaks into verlof/primary/allePl fan-out paths. Only real
      // work tasks generate the fourth-pass summary in other PL sections;
      // EasyKit verlof should stay detail-only under Hendrika.
      const plGroup = ensurePl(EASYKIT_PL_ID, easykitPlName);
      for (const instId of instIds) {
        const instName = instId === 0 ? 'Niet toegewezen' : (userMap.get(instId) ?? `Gebruiker ${instId}`);
        const installer = ensureInstaller(plGroup, instId, instName);
        const existing = installer.cells.get(dateKey);
        if (existing) existing.push(task);
        else installer.cells.set(dateKey, [task]);
      }
      continue;
    }

    if (isVerlofTask(task)) {
      verlofShared.push(task);
      continue;
    }

    // Resolve projectleider
    const plId = task.x_studio_projectleider
      ? task.x_studio_projectleider[0]
      : 0;
    const plName = task.x_studio_projectleider
      ? task.x_studio_projectleider[1]
      : 'Geen projectleider';

    const plGroup = ensurePl(plId, plName);

    // Resolve installers
    const installerIds =
      task.x_studio_installateur && task.x_studio_installateur.length > 0
        ? task.x_studio_installateur
        : [0]; // 0 = "Niet toegewezen"

    for (const instId of installerIds) {
      const instName = instId === 0 ? 'Niet toegewezen' : (userMap.get(instId) ?? `Gebruiker ${instId}`);
      const installer = ensureInstaller(plGroup, instId, instName);

      const existing = installer.cells.get(dateKey);
      if (existing) {
        existing.push(task);
      } else {
        installer.cells.set(dateKey, [task]);
      }
    }

    if (task.x_studio_alle_projectleiders) {
      allePlShared.push(task);
    }
  }

  // Second pass: add verlof tasks to EVERY PL group (one task in Odoo, visible in all groups)
  for (const task of verlofShared) {
    const dateKey = (task.date_deadline as string).slice(0, 10);
    const installerIds = task.x_studio_installateur!;
    const hasEasykitInstaller = installerIds.some((instId) => EASYKIT_INSTALLERS.has(instId));
    for (const [plId, plGroup] of plMap) {
      if (plId === EASYKIT_PL_ID && !hasEasykitInstaller) continue;
      for (const instId of installerIds) {
        const instName = instId === 0 ? 'Niet toegewezen' : (userMap.get(instId) ?? `Gebruiker ${instId}`);
        const installer = ensureInstaller(plGroup, instId, instName);
        const existing = installer.cells.get(dateKey);
        if (existing) {
          existing.push(task);
        } else {
          installer.cells.set(dateKey, [task]);
        }
      }
    }
  }

  // Third pass: "alle projectleiders" tasks → inject into every OTHER PL group
  for (const task of allePlShared) {
    const dateKey = (task.date_deadline as string).slice(0, 10);
    const primaryPlId = task.x_studio_projectleider ? task.x_studio_projectleider[0] : 0;
    const installerIds = task.x_studio_installateur?.length ? task.x_studio_installateur : [0];

    for (const [plId, plGroup] of plMap) {
      if (plId === primaryPlId) continue; // already in primary group from first pass
      for (const instId of installerIds) {
        const instName = instId === 0 ? 'Niet toegewezen' : (userMap.get(instId) ?? `Gebruiker ${instId}`);
        const installer = ensureInstaller(plGroup, instId, instName);
        const existing = installer.cells.get(dateKey);
        if (existing) {
          existing.push(task);
        } else {
          installer.cells.set(dateKey, [task]);
        }
      }
    }
  }

  // Fourth pass: one synthetic "Easykit tour" card per (installer, day),
  // fanned out to every non-Hendrika PL section so each PL sees a single
  // occupancy signal. Hendrika's section already holds the full detail.
  let syntheticId = -1;
  for (const [key, realTasks] of easykitTourPairs) {
    const [instIdStr, dateKey] = key.split(':');
    const instId = Number(instIdStr);
    if (FULLY_EASYKIT_INSTALLERS.has(instId)) continue;
    const summaryTask: OdooTask = {
      id: syntheticId--,
      name: `${realTasks.length} ${realTasks.length === 1 ? 'taak' : 'taken'}`,
      description: false,
      project_id: [0, 'Easykit tour'],
      user_ids: [],
      stage_id: false,
      date_deadline: dateKey,
      priority: '0',
      tag_ids: [],
      x_studio_installateur: instId === 0 ? [] : [instId],
      is_easykit: true,
      _easykitDayTasks: realTasks,
    };
    for (const [plId, plGroup] of plMap) {
      if (plId === EASYKIT_PL_ID) continue;
      const instName = instId === 0 ? 'Niet toegewezen' : (userMap.get(instId) ?? `Gebruiker ${instId}`);
      const installer = ensureInstaller(plGroup, instId, instName);
      const existing = installer.cells.get(dateKey);
      if (existing) existing.push(summaryTask);
      else installer.cells.set(dateKey, [summaryTask]);
    }
  }

  // Convert to output format, sorted by name
  const groups: ProjectleiderGroup[] = [];

  for (const [plId, plData] of plMap) {
    const installerRows: InstallerRow[] = [];
    for (const [instId, instData] of plData.installers) {
      installerRows.push({
        id: instId,
        name: instData.name,
        cells: instData.cells,
      });
    }
    // Sort installers: "Niet toegewezen" last, rest alphabetical
    installerRows.sort((a, b) => {
      if (a.id === 0) return 1;
      if (b.id === 0) return -1;
      return a.name.localeCompare(b.name, 'nl');
    });

    groups.push({ id: plId, name: plData.name, installerRows });
  }

  // Sort groups: Hendrika (EasyKit) and "Geen projectleider" to the bottom,
  // rest alphabetical. "Geen projectleider" sits below Hendrika.
  groups.sort((a, b) => {
    const aTail = a.id === 0 || a.id === EASYKIT_PL_ID;
    const bTail = b.id === 0 || b.id === EASYKIT_PL_ID;
    if (aTail && !bTail) return 1;
    if (bTail && !aTail) return -1;
    if (aTail && bTail) {
      if (a.id === 0) return 1;
      if (b.id === 0) return -1;
    }
    return a.name.localeCompare(b.name, 'nl');
  });

  if (showAllInstallers) {
    // Add empty rows for ALL installers in EVERY PL group
    for (const group of groups) {
      const existingIds = new Set(group.installerRows.map((r) => r.id));
      for (const [instId, instName] of userMap) {
        if (!existingIds.has(instId)) {
          group.installerRows.push({ id: instId, name: instName, cells: new Map() });
        }
      }
      // Re-sort: "Niet toegewezen" last, rest alphabetical
      group.installerRows.sort((a, b) => {
        if (a.id === 0) return 1;
        if (b.id === 0) return -1;
        return a.name.localeCompare(b.name, 'nl');
      });
    }

    // If no groups exist yet, create "Geen projectleider" with all installers
    if (groups.length === 0 && userMap.size > 0) {
      const allRows: InstallerRow[] = [];
      for (const [instId, instName] of userMap) {
        allRows.push({ id: instId, name: instName, cells: new Map() });
      }
      allRows.sort((a, b) => a.name.localeCompare(b.name, 'nl'));
      groups.push({ id: 0, name: 'Geen projectleider', installerRows: allRows });
    }
  }

  return groups;
}

/**
 * Sort priority for installer rows in flat view.
 * Lower = higher up in the list (top).
 *
 * 1 — onderaannemers (non-employees)
 * 2 — other Thermoduct employees
 * 3 — Guy Berchmans
 * 4 — Dries Vercruyssen
 * 5 — Oliver Coosemans
 * 6 — Niet toegewezen (always last)
 */
function getInstallerSortPriority(
  id: number,
  name: string,
  employeeMap?: Map<number, boolean>,
): number {
  if (id === 0) return 6;
  const lower = name.toLowerCase();
  if (lower.includes('coosemans')) return 5;
  if (lower.includes('vercruyssen')) return 4;
  if (lower.includes('berchmans')) return 3;
  if (employeeMap?.get(id)) return 2;
  return 1; // onderaannemer
}

/**
 * Flat installer grouping — no projectleider headers.
 * All installers merged into one list, sorted alphabetically.
 * Returns a single group so the grid renders without PL headers.
 */
export function groupTasksFlatInstallers(
  tasks: OdooTask[],
  weekDays: Date[],
  userMap: Map<number, string>,
  showAllInstallers?: boolean,
  employeeMap?: Map<number, boolean>,
  easykitConfig?: EasykitGroupingConfig | null,
): InstallerRow[] {
  const validDateKeys = new Set(weekDays.map(formatDateKey));

  const weekTasks = tasks.filter(
    (t) => typeof t.date_deadline === 'string'
      && validDateKeys.has(t.date_deadline.slice(0, 10))
      && !isFeestdagMarker(t),
  );

  // Group directly by installer (skip PL layer)
  const installerMap = new Map<number, { name: string; cells: Map<string, OdooTask[]> }>();
  // Track real EasyKit tasks per (installer, day) for tour card consolidation
  const easykitDayTasks = new Map<string, OdooTask[]>();

  for (const task of weekTasks) {
    const dateKey = (task.date_deadline as string).slice(0, 10);

    const installerIds =
      task.x_studio_installateur && task.x_studio_installateur.length > 0
        ? task.x_studio_installateur
        : [0];

    for (const instId of installerIds) {
      // Skip installers not in the userMap (e.g. subcontractors when filtered to employees)
      if (instId !== 0 && !userMap.has(instId)) continue;
      const instName = instId === 0 ? 'Niet toegewezen' : (userMap.get(instId) ?? `Gebruiker ${instId}`);

      if (task.is_easykit && easykitConfig) {
        const pairKey = `${instId}:${dateKey}`;
        const existing = easykitDayTasks.get(pairKey);
        if (existing) existing.push(task);
        else easykitDayTasks.set(pairKey, [task]);
        continue;
      }

      if (!installerMap.has(instId)) {
        installerMap.set(instId, { name: instName, cells: new Map() });
      }
      const installer = installerMap.get(instId)!;
      const existing = installer.cells.get(dateKey);
      if (existing) {
        existing.push(task);
      } else {
        installer.cells.set(dateKey, [task]);
      }
    }
  }

  // Inject synthetic tour cards for consolidated EasyKit tasks
  let syntheticId = -1000;
  for (const [pairKey, realTasks] of easykitDayTasks) {
    const [instIdStr, dateKey] = pairKey.split(':');
    const instId = Number(instIdStr);
    const instName = instId === 0 ? 'Niet toegewezen' : (userMap.get(instId) ?? `Gebruiker ${instId}`);

    const summaryTask: OdooTask = {
      id: syntheticId--,
      name: `${realTasks.length} ${realTasks.length === 1 ? 'taak' : 'taken'}`,
      description: false,
      project_id: [0, 'Easykit tour'],
      user_ids: [],
      stage_id: false,
      date_deadline: dateKey,
      priority: '0',
      tag_ids: [],
      x_studio_installateur: instId === 0 ? [] : [instId],
      is_easykit: true,
      _easykitDayTasks: realTasks,
    };

    if (!installerMap.has(instId)) {
      installerMap.set(instId, { name: instName, cells: new Map() });
    }
    const installer = installerMap.get(instId)!;
    const existing = installer.cells.get(dateKey);
    if (existing) existing.push(summaryTask);
    else installer.cells.set(dateKey, [summaryTask]);
  }

  if (showAllInstallers) {
    for (const [instId, instName] of userMap) {
      if (!installerMap.has(instId)) {
        installerMap.set(instId, { name: instName, cells: new Map() });
      }
    }
  }

  const rows: InstallerRow[] = [];
  for (const [instId, instData] of installerMap) {
    rows.push({ id: instId, name: instData.name, cells: instData.cells });
  }

  // Sort: onderaannemers top → other employees → Guy → Dries → Oliver → Niet toegewezen
  rows.sort((a, b) => {
    const pa = getInstallerSortPriority(a.id, a.name, employeeMap);
    const pb = getInstallerSortPriority(b.id, b.name, employeeMap);
    if (pa !== pb) return pa - pb;
    return a.name.localeCompare(b.name, 'nl');
  });

  return rows;
}

/**
 * Count unique task IDs visible in the current grid.
 * Works for both PL+Inst groups and flat installer rows.
 */
export function countVisibleTasks(
  groups?: ProjectleiderGroup[],
  flatRows?: InstallerRow[],
): number {
  const seen = new Set<number>();

  if (groups) {
    for (const group of groups) {
      for (const row of group.installerRows) {
        for (const tasks of row.cells.values()) {
          for (const t of tasks) seen.add(t.id);
        }
      }
    }
  }

  if (flatRows) {
    for (const row of flatRows) {
      for (const tasks of row.cells.values()) {
        for (const t of tasks) seen.add(t.id);
      }
    }
  }

  return seen.size;
}
